import React, {useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {useOrganization} from "../organizations/organization-context";
import {getAncestors, getSubscriptionsCount} from "../../api/organization-unit-functions";
import {
    CircularProgress,
    Link,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    useMediaQuery
} from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {ArrowForwardIos} from "@material-ui/icons";
import {run} from "../../utils/function-util";
import {httpGet} from "../../api/http-functions";
import * as _ from 'lodash';
import Tooltip from '@material-ui/core/Tooltip';
import {useApiNotification} from "../ui/ApiNotificationProvider";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import {ExportMembers} from "../members/shared/actions/ExportMembers";

const useStyles = makeStyles(theme => ({
    spaceBetweenFlexContainer: {
        'display': 'flex',
        'justify-content': 'space-between',
        'align-items': 'center',
    },
    spaceEvenlyFlexContainer: {
        'display': 'flex',
        'justify-content': 'space-evenly',
        'align-items': 'center',
    },
    childOrgUnitOverview: {
        'padding': '5px 20px',
        [theme.breakpoints.down('sm')]: {
            'flex-direction': 'column',
            'padding': '10px'
        }
    },
    childOrgUnitContainer: {
        'display': 'flex',
        'flex-grow': 1
    },
    childOrgUnitName: {
        'margin': '0 10px',
        'overflow': 'hidden'
    },
    childOrgUnitInfo: {
        'flex-basis': '30%'
    },
    childOrgUniSubscriptions: {
        'flex-basis': '70%',
        'text-align': 'center',
    },
    childOrgUnitOpen: {
        'flex-basis': '30%',
    },
    childPrefixArrow: {
        'top': '-2px',
        position: 'relative'
    },
    nameDivider: {
        [theme.breakpoints.down('sm')]: {
            'margin': '5px'
        }
    },
    openLink: {
        'display': 'inline-flex'
    },
    openText: {
        'margin': 'auto'
    },
    progressBarContainer: {
        'padding': '20px',
        'height': '100%'
    }
}));

const withCache = (httpRequestFn: any, keyFn: any) => {
    let cache = {};

    return async function (...args: any[]) {
        const key = keyFn.apply(null, args);
        if (_.has(cache, key)) {
            const result = _.get(cache, key);
            return Promise.resolve(result);
        } else {
            const result = await httpRequestFn.apply(null, args);
            _.set(cache, key, result);
            return result;
        }
    }
}


export const withCachedOrganizationUnitSubscriptionsRowOverview = () => {
    const cachedGetAncestor = withCache(getAncestors(httpGet),
        ({
             organizationId,
             organizationUnitId,
             recursive
         }: any) => organizationId + '_' + organizationUnitId + '_' + recursive);

    const cachedGetSubscriptionsCounts = withCache(getSubscriptionsCount,
        (organizationId: any, organizationUnitId: any, recursive: any) => organizationId + '_' + organizationUnitId + '_' + recursive)

    return ({
                organizationUnitId,
                recursive
            }: any) => (
        <OrganizationUnitSubscriptionsRowOverview organizationUnitId={organizationUnitId}
                                                  recursive={recursive}
                                                  getAncestorsFn={cachedGetAncestor}
                                                  getSubscriptionsCountFn={cachedGetSubscriptionsCounts}/>);
}

export const OrganizationUnitSubscriptionsRowOverview = ({
                                                             organizationUnitId,
                                                             recursive,
                                                             getAncestorsFn = getAncestors(httpGet),
                                                             getSubscriptionsCountFn = getSubscriptionsCount
                                                         }: any) => {
    const classes = useStyles();
    const [currentOrganization] = useOrganization();
    const {handleError} = useApiNotification();
    const [subscriptionsCounts, setSubscriptionsCounts] = useState({});
    const [ancestors, setAncestors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(run(async () => {
            const subCounts = !organizationUnitId ? [] :
                await getSubscriptionsCountFn(currentOrganization?.id, organizationUnitId, recursive);
            return subCounts;
        },
        (availabilityCounts: any) => {
            const displayedSubscriptionsCounts =
                availabilityCounts.reduce(
                    (acc: any, curr: any) => ({
                        available: acc.available + curr.availableSubscriptions,
                        used: acc.used + curr.usedSubscriptions
                    }),
                    {available: 0, used: 0}
                );

            setSubscriptionsCounts(displayedSubscriptionsCounts);
        },
        handleError),
        [currentOrganization, organizationUnitId]);

    useEffect(run(async () => {
            const parentOrgUnits = !organizationUnitId ? [] :
                await getAncestorsFn({
                    organizationId: currentOrganization?.id,
                    organizationUnitId,
                    recursive: true
                });

            return parentOrgUnits;
        },
        setAncestors,
        handleError),
        [organizationUnitId, currentOrganization])

    useEffect(() => {
        setIsLoading(_.isEmpty(subscriptionsCounts) || ancestors.length === 0);
    }, [subscriptionsCounts, ancestors]);

    return (
        isLoading ?
            <div className={classes.progressBarContainer}>
                <CircularProgress/>
            </div> :
            <DumbOrganizationUnitSubscriptionsRowOverview organizationUnitId={organizationUnitId}
                                                          subscriptionCounts={subscriptionsCounts}
                                                          ancestors={ancestors}
                                                          isChild={recursive}/>
    );
};

export const DumbOrganizationUnitSubscriptionsRowOverview = ({
                                                                 organizationUnitId,
                                                                 subscriptionCounts,
                                                                 ancestors,
                                                                 isChild
                                                             }: any) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const notMobile = useMediaQuery(theme => (theme as any).breakpoints.up('sm'));
    const fullPath = ancestors.map((i: any) => i.name).join(' / ');
    const childPrefixArrow = <span className={classes.childPrefixArrow}>↳</span>
    const [contextMenuAnchor, setContextMenuAnchor] = React.useState(null);
    const [exportMembersToCsv, setExportMembersToCsv] = useState(false);

    const onContextMenuClicked = (event: any) => {
        setContextMenuAnchor(event.currentTarget);
    };

    const onContextMenuClosed = (event: any) => {
        setContextMenuAnchor(null);
    };

    const exportUsersToCsvClicked = (event: any) => {
        setContextMenuAnchor(null);
        setExportMembersToCsv(true);
    }

    return (
        <div className={clsx(notMobile && classes.spaceBetweenFlexContainer, classes.childOrgUnitOverview)}>
            <ListItem classes={{
                container: classes.childOrgUnitContainer,
                root: classes.childOrgUnitName
            }}>
                <ListItemText>
                    <Tooltip title={fullPath}>
                        <span>{isChild ? childPrefixArrow : '...'} / {ancestors[ancestors.length - 1].name}</span>
                    </Tooltip>
                </ListItemText>

                <ListItemSecondaryAction>
                    <MoreHorizIcon
                        onClick={onContextMenuClicked}
                    />
                    <Menu
                        anchorEl={contextMenuAnchor}
                        keepMounted
                        open={Boolean(contextMenuAnchor)}
                        onClose={onContextMenuClosed}
                    >
                        <MenuItem onClick={exportUsersToCsvClicked}>
                          {t('Members.ExportCsv')}
                        </MenuItem>
                    </Menu>
                </ListItemSecondaryAction>
            </ListItem>
            <Divider className={classes.nameDivider} flexItem={notMobile}
                     orientation={notMobile ? "vertical" : "horizontal"}/>
            <div className={clsx(classes.spaceBetweenFlexContainer, classes.childOrgUnitInfo)}>
                <div className={classes.childOrgUniSubscriptions}>
                    <div className={classes.spaceEvenlyFlexContainer}>
                        <div>
                            <div>{t("_Common.Labels.Available")}</div>
                            <div>{subscriptionCounts && subscriptionCounts.available}</div>
                        </div>
                        <Divider flexItem={true} orientation="vertical" light/>
                        <div>
                            <div>{t("_Common.Labels.Used")}</div>
                            <div>{subscriptionCounts && subscriptionCounts.used}</div>
                        </div>
                    </div>
                </div>
                <Divider flexItem={true} orientation="vertical"/>
                <div className={clsx(classes.childOrgUnitOpen, classes.spaceEvenlyFlexContainer)}>
                    <Link component={RouterLink} to={`/organization-units/${organizationUnitId}/detail?tab=0`}
                          color="primary"
                          className={classes.openLink}>
                        <div className={classes.openText}>{t('OrganizationUnits.Menu.Explore')}</div>
                        <ArrowForwardIos color="primary"/>
                    </Link>
                </div>
            </div>
            {
                exportMembersToCsv && <ExportMembers
                    organizationUnitId={organizationUnitId}
                    onError={() => setExportMembersToCsv(false)}
                    onDone={() => setExportMembersToCsv(false)}
                />
            }
        </div>
    );
};
